import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Button } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { generateUuid } from '../../../shared/utils';
import CreateMouFromTemplateModal from '../../mous/CreateMouFromTemplateModal';
import {
  allMousForAdminQuery,
  extractMousForAdmin,
} from '../../mous/constants';

// Partner type code -> MOU role, so the create flow pre-filters templates by type.
const PARTNER_TYPE_TO_MOU_ROLE = {
  C: 'REGIONAL_COORDINATOR',
  F: 'FIELD_WORKER',
  T: 'TRAINER',
};

function pickBestMou(mous) {
  if (!mous.length) return null;
  const signed = mous.find((m) => m.status === 'SIGNED');
  if (signed) return signed;
  return [...mous].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  )[0];
}

// Renders on the commitment-form (COMMITMENT_FORM_SIGNED) onboarding task:
// "View PCF" when a Partnership Commitment Form (MOU) already exists for the
// contact, or "Create PCF" (pre-filled contact + role) when none does.
function CommitmentFormAction({ contactId, partnerType }) {
  const location = useLocation();
  const history = useHistory();
  const fromPath = `${location.pathname}${location.search || ''}`;

  const { data, loading } = useQuery(allMousForAdminQuery, {
    variables: { first: 50, filters: { contactId, isTemplate: false } },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  });

  const existing = useMemo(() => {
    const conn = data ? extractMousForAdmin(data) : null;
    return pickBestMou((conn?.edges || []).map((e) => e.node));
  }, [data]);

  const createId = useMemo(() => generateUuid(), []);
  const [creating, setCreating] = useState();

  const handleCreate = useCallback(() => {
    setCreating({
      createId,
      defaultVersionNo: 1,
      initialContactId: contactId,
      templateRole: PARTNER_TYPE_TO_MOU_ROLE[partnerType],
    });
  }, [createId, contactId, partnerType]);

  const handleCreateFinish = useCallback(() => {
    history.push(`/mous/view/${createId}?from=${encodeURIComponent(fromPath)}`);
  }, [history, createId, fromPath]);

  if (loading && !data) return null;

  if (existing) {
    return (
      <Link
        to={`/mous/view/${existing._id}?from=${encodeURIComponent(fromPath)}`}
        style={{ marginLeft: 8 }}
      >
        <Button size="small" icon={<EyeOutlined />}>
          View PCF
        </Button>
      </Link>
    );
  }

  return (
    <>
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={handleCreate}
        style={{ marginLeft: 8 }}
      >
        Create PCF
      </Button>
      <CreateMouFromTemplateModal
        visible={creating}
        onFinish={handleCreateFinish}
        onCancel={() => setCreating(false)}
      />
    </>
  );
}

export default CommitmentFormAction;
